import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, List, ListItem, ListItemIcon, ListItemText, Stack, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AppDialogTitle } from '../AppDialogTitle';
import { featureLabel, limitLabel, type PlanGate } from '../../lib/planGates';
import { formatCount, planAha, planBenefitsIntro, planFor, planPriceLabel } from '../../lib/planCatalog';

interface UpgradeDialogProps {
  gate: PlanGate | null;
  canManageBilling?: boolean;
  onSeePlans: (gate: PlanGate) => void;
  onClose: () => void;
}

function capitalize(text: string) {
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

/** Headline naming what is blocked: "Webhooks are a Pro feature" / "You've used 5 of 5 surveys". */
export function upgradeHeadline(gate: PlanGate): string {
  const plan = planFor(gate.requiredTier);
  if (gate.kind === 'feature') {
    const label = featureLabel(gate.feature);
    return `${capitalize(label)} ${/s$/.test(label) && !/^(this|a|an)\b/i.test(label) ? 'are' : 'is'} included in ${plan.name}`;
  }
  const what = limitLabel(gate.limit);
  if (gate.limitedByTrial) return `Extra ${what} aren't included in the free trial`;
  if (gate.current != null && gate.max != null) return `You've used ${formatCount(gate.current)} of ${formatCount(gate.max)} ${what}`;
  if (gate.max != null) return `Your plan includes ${formatCount(gate.max)} ${what}`;
  return `You've reached your plan's ${what} limit`;
}

/** One value-led prompt for every plan 403: what is blocked, what the plan adds, and its price. */
export function UpgradeDialog({ gate, canManageBilling = true, onSeePlans, onClose }: UpgradeDialogProps) {
  const plan = gate ? planFor(gate.requiredTier) : null;
  const benefits = plan ? plan.benefits.filter((benefit) => !/trial/i.test(benefit)).slice(0, 4) : [];
  const limitQuota = gate?.kind === 'limit' && plan ? quotaFor(plan.quotas as unknown as Record<string, number | null | undefined>, gate.limit) : undefined;

  return (
    <Dialog open={!!gate} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="upgrade-dialog-title" scroll="body">
      <AppDialogTitle id="upgrade-dialog-title" onClose={onClose}>{gate && plan ? `Upgrade to ${plan.name}` : 'Upgrade'}</AppDialogTitle>
      {gate && plan ? (
        <DialogContent sx={{ pt: '20px !important' }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <LockOutlinedIcon color="warning" fontSize="small" />
            <Typography fontWeight={800} data-testid="upgrade-headline">{upgradeHeadline(gate)}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {planAha(plan)}{/[.!?]$/.test(planAha(plan)) ? '' : '.'}
            {limitQuota !== undefined ? ` ${plan.name} includes ${limitQuota == null ? 'a custom allowance' : formatCount(limitQuota)} ${limitLabel(gate.kind === 'limit' ? gate.limit : '')}.` : ''}
            {gate.kind === 'limit' && gate.periodEnd && !Number.isNaN(new Date(gate.periodEnd).getTime())
              ? ` Or wait: your allowance resets on ${new Date(gate.periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.`
              : ''}
          </Typography>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1} gap={1} flexWrap="wrap">
              <Typography fontWeight={800}>{plan.name}</Typography>
              <Chip size="small" color="primary" label={planPriceLabel(plan)} />
            </Stack>
            {planBenefitsIntro(plan) ? <Typography variant="caption" color="text.secondary" fontWeight={700}>{planBenefitsIntro(plan)}</Typography> : null}
            <List dense disablePadding>
              {benefits.map((benefit) => (
                <ListItem key={benefit} disableGutters sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={benefit} />
                </ListItem>
              ))}
            </List>
          </Box>
          {!canManageBilling && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
              Ask a workspace owner or admin to choose a plan.
            </Typography>
          )}
        </DialogContent>
      ) : null}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Not now</Button>
        <Button variant="contained" onClick={() => gate && onSeePlans(gate)} autoFocus>See plans</Button>
      </DialogActions>
    </Dialog>
  );
}

function quotaFor(quotas: Record<string, number | null | undefined>, limit: string): number | null | undefined {
  const key = {
    surveys: 'surveys',
    responses: 'responsesPerMonth',
    responsesPerMonth: 'responsesPerMonth',
    aiGenerations: 'aiGenerationsPerMonth',
    aiGenerationsPerMonth: 'aiGenerationsPerMonth',
    emailInvites: 'emailInvitesPerMonth',
    emailInvitesPerMonth: 'emailInvitesPerMonth',
    workspaces: 'workspaces',
    collaborators: 'collaborators',
    teamMembers: 'teamMembers',
    members: 'teamMembers',
    seats: 'teamMembers',
  }[limit];
  return key ? quotas[key] : undefined;
}
